import { FC, useCallback, useMemo } from 'react';

import { isValid } from 'date-fns';
import { FieldType, FormConfigItem, RequisitesFormName } from 'utils/constants/requisites';
import { formatDate } from 'utils/date';

import {
    createStaticDataProvider,
    FieldsGroupHelper,
    FormLabel,
    Input,
    NavigationBar,
    NumberInput,
    Select,
    VSpacing,
} from '@hh.ru/magritte-ui';
import { CrossOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import useTrls from 'hooks/translation/useTrls';

import { DateSelect } from 'components/common/DateSelect/DateSelect';
import getFieldPath from 'components/RequisitesFormModal/getFieldPath';

import { Action } from 'store/createReducer';
import { setRequisiteErrorByFieldPath, setRequisitesFormFieldByName } from 'store/models/requisites';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/RequisitesFormModal/RequisitesItem/RequisitesItem.module.less';

const TODAY = new Date();

interface RequisitesItemProps extends FormConfigItem {
    isFieldDisabled: boolean;
    requisiteFormName: RequisitesFormName;
}

export const RequisitesItem: FC<RequisitesItemProps> = ({
    fieldType,
    fieldName,
    isShort,
    label,
    placeholder,
    error,
    values,
    labelValues,
    isFieldDisabled,
    requisiteFormName,
    getTip,
    isNumericInput,
    dataQa,
}) => {
    const { requisites, requisitesErrors } = useSelector((state) => state.requisites);

    const requisitesForm = requisites[requisiteFormName];
    const dispatch = useDispatch();
    const trl = useTrls();

    const fieldPath = getFieldPath(fieldName, requisiteFormName);
    const fieldValue =
        requisitesForm && requisitesForm.hasOwnProperty(fieldName)
            ? requisitesForm[fieldName as keyof typeof requisitesForm]
            : null;

    const fieldError = requisitesErrors[fieldPath];
    const fieldErrorTrl = fieldError && error?.[fieldError];

    const tipText = useMemo(() => getTip?.(requisites) || '', [getTip, requisites]);

    const dataProvider = useMemo(() => {
        return createStaticDataProvider(
            (values ?? []).map((value) => ({
                value,
                text: trl(labelValues?.[value] || ''),
            }))
        );
    }, [values, labelValues, trl]);

    const handleChange = useCallback(
        (value: string | boolean) => {
            const actions: Action[] = [setRequisitesFormFieldByName({ requisiteFormName, fieldName, value })];
            if (fieldError) {
                actions.push(setRequisiteErrorByFieldPath({ fieldPath, value: null }));
            }
            dispatch(actions);
        },
        [dispatch, fieldError, fieldName, fieldPath, requisiteFormName]
    );

    const handleChangeSelect = useCallback(
        (value: { value: string; text: string }) => {
            handleChange(value.value);
        },
        [handleChange]
    );

    const labelComponent = (
        <>
            <FormLabel info={tipText && trl(tipText)}>{trl(label)}</FormLabel>
            <VSpacing default={12} />
        </>
    );

    let content = null;

    if (
        fieldType === FieldType.Select &&
        (typeof fieldValue === 'string' || fieldValue === null || fieldValue === undefined)
    ) {
        content = (
            <>
                {labelComponent}
                {labelValues && values && (
                    <Select
                        type="radio"
                        value={
                            fieldValue
                                ? {
                                      value: fieldValue,
                                      text: trl(labelValues?.[fieldValue] || ''),
                                  }
                                : undefined
                        }
                        dataProvider={dataProvider}
                        onChange={handleChangeSelect}
                        bottomSheetHeight="content"
                        renderHeader={({ onCloseBottomSheet }) => (
                            <NavigationBar
                                title={trl(label)}
                                right={<CrossOutlinedSize24 onClick={onCloseBottomSheet} />}
                            />
                        )}
                        triggerProps={{
                            size: 'medium',
                            label: trl(placeholder),
                            invalid: !!fieldError,
                            disabled: isFieldDisabled,
                            stretched: true,
                            'data-qa': dataQa,
                        }}
                    />
                )}
            </>
        );
    }

    if (fieldType === FieldType.Input && (typeof fieldValue === 'string' || fieldValue === null)) {
        content = (
            <>
                {labelComponent}
                {isNumericInput ? (
                    <NumberInput
                        value={fieldValue || ''}
                        onChange={handleChange}
                        placeholder={placeholder && trl(placeholder)}
                        invalid={!!fieldError}
                        disabled={isFieldDisabled}
                        clearableOnFocus
                        decimalMark={[]}
                        decimalLength={0}
                        allowNegative={false}
                        groupSeparator=""
                        data-qa={dataQa}
                    />
                ) : (
                    <Input
                        value={fieldValue || ''}
                        onChange={handleChange}
                        placeholder={placeholder && trl(placeholder)}
                        invalid={!!fieldError}
                        disabled={isFieldDisabled}
                        clearableOnFocus
                        data-qa={dataQa}
                    />
                )}
            </>
        );
    }

    if (fieldType === FieldType.Calendar && (typeof fieldValue === 'string' || fieldValue === null)) {
        content = (
            <>
                {labelComponent}
                <DateSelect
                    maxDate={TODAY}
                    onDateSelect={(value) => {
                        const date = new Date(value);

                        if (isValid(date)) {
                            handleChange(formatDate(date));
                        }
                    }}
                    placeholder={placeholder ? trl(placeholder) : ''}
                    selectedDate={fieldValue ? new Date(fieldValue) : undefined}
                    inputDisabled={isFieldDisabled}
                    inputInvalid={!!fieldError}
                    data-qa={dataQa}
                />
            </>
        );
    }

    return (
        <div className={isShort ? styles.requisitesFormItemShort : styles.requisitesFormItem}>
            <FieldsGroupHelper disabled={isFieldDisabled} invalid={!!fieldError} errorMessage={trl(fieldErrorTrl)}>
                {content}
            </FieldsGroupHelper>
        </div>
    );
};
